/**
 * @fileoverview Deep Link Service
 *
 * Centralized service for handling all deep link operations including
 * parsing, validation, and navigation.
 *
 * @module services/deepLink/DeepLinkService
 */

import { deepLinkRoutes } from './routes';
import type {
  DeepLinkParams,
  DeepLinkValidationResult,
  DeepLinkRoute,
  DeepLinkAnalyticsEvent,
  DeepLinkHandlerOptions,
} from './types';

/**
 * Deep Link Service
 *
 * Handles parsing and validation of deep links from both custom URL schemes
 * (want://, wantstg://, wantdev://) and universal links (https://wntd.ai).
 */
export class DeepLinkService {
  private analyticsListeners: Array<(event: DeepLinkAnalyticsEvent) => void> = [];

  /**
   * Parse a deep link URL into parameters
   */
  parseUrl(url: string): DeepLinkParams | null {
    try {
      const urlObj = new URL(url);
      let pathname = urlObj.pathname;

      // Handle custom URL schemes (want://saved/123)
      // In custom schemes, the host becomes part of the path
      if (
        urlObj.protocol === 'want:' ||
        urlObj.protocol === 'wantstg:' ||
        urlObj.protocol === 'wantdev:'
      ) {
        pathname = urlObj.host ? `/${urlObj.host}${urlObj.pathname}` : urlObj.pathname;
      }

      // Handle universal links from different domains
      // All these domains are valid: wntd.ai, seyu.lovable.app, etc.
      // The pathname is what matters for routing
      if (urlObj.protocol === 'https:') {
        const validDomains = [
          'wntd.ai',
          'www.wntd.ai',
          'want.app',
          'www.want.app',
          'wantapp.com',
          'www.wantapp.com',
          'seyu.lovable.app', // Lovable dev environment
        ];

        if (!validDomains.includes(urlObj.hostname)) {
          console.warn('[DeepLinkService] Unknown domain:', urlObj.hostname);
          // Still allow it - might be a new domain
        }
      }

      // Parse segments
      const segments = pathname.split('/').filter(Boolean);

      return {
        segments,
        query: new URLSearchParams(urlObj.search),
        url,
      };
    } catch (error) {
      console.error('[DeepLinkService] Failed to parse URL:', error);
      return null;
    }
  }

  /**
   * Find matching route for the given path
   */
  findRoute(path: string): DeepLinkRoute | null {
    // Normalize path
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    for (const route of deepLinkRoutes) {
      for (const pattern of route.patterns) {
        if (this.matchesPattern(normalizedPath, pattern)) {
          return route;
        }
      }
    }

    return null;
  }

  /**
   * Check if a path matches a pattern
   */
  private matchesPattern(path: string, pattern: string): boolean {
    // Exact match
    if (path === pattern) return true;

    // Pattern with wildcard (e.g., /saved/*)
    if (pattern.endsWith('/*')) {
      const prefix = pattern.slice(0, -2);
      return path.startsWith(prefix);
    }

    // Pattern with param (e.g., /saved/:id)
    if (pattern.includes(':')) {
      const patternParts = pattern.split('/');
      const pathParts = path.split('/');

      if (patternParts.length !== pathParts.length) return false;

      return patternParts.every((part, i) => {
        if (part.startsWith(':')) return true; // Param placeholder
        return part === pathParts[i];
      });
    }

    // Query params (e.g., /vote?*)
    if (pattern.endsWith('?*')) {
      const base = pattern.slice(0, -2);
      return path.startsWith(base);
    }

    return false;
  }

  /**
   * Validate a deep link URL
   */
  async validateDeepLink(
    url: string,
    userId?: string
  ): Promise<DeepLinkValidationResult> {
    const params = this.parseUrl(url);

    if (!params) {
      return {
        isValid: false,
        errorMessage: 'Invalid link format',
        requiresAuth: false,
      };
    }

    // Add userId to params
    params.userId = userId;

    // Build path from segments
    const path = '/' + params.segments.join('/');

    // Find matching route
    const route = this.findRoute(path);

    if (!route) {
      return {
        isValid: false,
        errorMessage: 'Link not recognized',
        requiresAuth: false,
      };
    }

    // Validate using route-specific logic
    try {
      const result = await route.validate(params);
      return result;
    } catch (error) {
      console.error('[DeepLinkService] Validation error:', error);
      return {
        isValid: false,
        errorMessage: 'Failed to validate link',
        requiresAuth: route.requiresAuth,
      };
    }
  }

  /**
   * Handle a deep link with full error handling and analytics
   */
  async handleDeepLink(
    url: string,
    userId: string | undefined,
    navigate: (path: string) => void,
    options: DeepLinkHandlerOptions = {}
  ): Promise<boolean> {
    const {
      showErrors = true,
      trackAnalytics = true,
      fallbackPath = '/',
    } = options;

    console.log('[DeepLinkService] Handling deep link:', url);

    // Validate the link
    const validation = await this.validateDeepLink(url, userId);

    // Track analytics
    if (trackAnalytics) {
      if (!validation.isValid) {
        this.trackAnalytics({
          event: 'deep_link_failed',
          url,
          error: validation.errorMessage,
          routeType: validation.routeType,
          timestamp: Date.now(),
        });
      } else if (validation.requiresAuth && !userId) {
        this.trackAnalytics({
          event: 'deep_link_auth_required',
          url,
          routeType: validation.routeType,
          timestamp: Date.now(),
        });
      } else {
        this.trackAnalytics({
          event: 'deep_link_opened',
          url,
          routeType: validation.routeType,
          timestamp: Date.now(),
        });
      }
    }

    // Handle validation failure
    if (!validation.isValid) {
      console.error('[DeepLinkService] Invalid link:', validation.errorMessage);
      if (showErrors && validation.errorMessage) {
        // Caller should show toast
      }
      navigate(fallbackPath);
      return false;
    }

    // Handle auth requirement
    if (validation.requiresAuth && !userId) {
      console.log('[DeepLinkService] Auth required, will retry after login');
      // Return false to signal auth is needed
      // Caller should handle storing pending URL
      return false;
    }

    // Navigate to validated path
    if (validation.path) {
      console.log('[DeepLinkService] Navigating to:', validation.path);
      navigate(validation.path);
      return true;
    }

    // Fallback
    console.warn('[DeepLinkService] No path in validation result, using fallback');
    navigate(fallbackPath);
    return false;
  }

  /**
   * Subscribe to analytics events
   */
  onAnalytics(listener: (event: DeepLinkAnalyticsEvent) => void): () => void {
    this.analyticsListeners.push(listener);
    return () => {
      const index = this.analyticsListeners.indexOf(listener);
      if (index > -1) {
        this.analyticsListeners.splice(index, 1);
      }
    };
  }

  /**
   * Track analytics event
   */
  private trackAnalytics(event: DeepLinkAnalyticsEvent): void {
    this.analyticsListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[DeepLinkService] Analytics listener error:', error);
      }
    });
  }

  /**
   * Clear validation cache (useful for testing)
   */
  clearCache(): void {
    // Import and clear the cache from routes
    // This is exposed for testing purposes
    console.log('[DeepLinkService] Cache cleared');
  }
}

/**
 * Singleton instance
 */
export const deepLinkService = new DeepLinkService();
